import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Calendar feeds — the shared contract and iCalendar helper for B49, BE-49.2.
 *
 * The feed URL is a credential: anyone holding it can read card titles and due
 * dates without a session. The authenticated JSON shape therefore exposes the
 * URL only for the caller's own per-board token, and no activity payload should
 * ever carry it.
 */

export const CALENDAR_FEED_TOKEN_MAX_LENGTH = 64;

export const calendarFeedTokenSchema = z
  .string()
  .min(20)
  .max(CALENDAR_FEED_TOKEN_MAX_LENGTH)
  .regex(/^[A-Za-z0-9_-]+$/, 'must be a URL-safe feed token');

export const boardCalendarFeedSchema = z.strictObject({
  boardId: ulidSchema,
  enabled: z.boolean(),
  hasToken: z.boolean(),
  feedUrl: z.string().url().nullable(),
});

export type BoardCalendarFeed = z.infer<typeof boardCalendarFeedSchema>;

export const boardCalendarFeedPatchSchema = z.strictObject({
  enabled: z.boolean(),
});

export type BoardCalendarFeedPatch = z.infer<typeof boardCalendarFeedPatchSchema>;

export const calendarFeedIcsEventSchema = z.strictObject({
  uid: z.string().min(1),
  title: z.string().min(1),
  startAt: z.string().min(1).nullable(),
  dueAt: z.string().min(1),
  updatedAt: z.string().min(1),
  url: z.string().url().nullable(),
});

export type CalendarFeedIcsEvent = z.infer<typeof calendarFeedIcsEventSchema>;

export const calendarFeedRenderInputSchema = z.strictObject({
  calendarName: z.string().min(1),
  generatedAt: z.date().optional(),
  events: z.array(calendarFeedIcsEventSchema),
});

export type CalendarFeedRenderInput = z.infer<typeof calendarFeedRenderInputSchema>;

export function calendarFeedPathForToken(token: string): string {
  return `/calendar-feeds/boards/${token}.ics`;
}

export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\r|\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');
}

export function formatIcsDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.valueOf())) {
    throw new Error('Invalid iCalendar date');
  }
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
}

function foldIcsLine(line: string): string[] {
  const folded: string[] = [];
  let rest = line;
  while (rest.length > 75) {
    folded.push(rest.slice(0, 75));
    rest = ` ${rest.slice(75)}`;
  }
  folded.push(rest);
  return folded;
}

function pushLine(lines: string[], line: string): void {
  lines.push(...foldIcsLine(line));
}

export function renderCalendarFeed(input: CalendarFeedRenderInput): string {
  const parsed = calendarFeedRenderInputSchema.parse(input);
  const generatedAt = formatIcsDate(parsed.generatedAt ?? new Date());
  const lines: string[] = [];

  pushLine(lines, 'BEGIN:VCALENDAR');
  pushLine(lines, 'VERSION:2.0');
  pushLine(lines, 'PRODID:-//Molt//Board Calendar Feed//EN');
  pushLine(lines, 'CALSCALE:GREGORIAN');
  pushLine(lines, 'METHOD:PUBLISH');
  pushLine(lines, `X-WR-CALNAME:${escapeIcsText(parsed.calendarName)}`);

  for (const event of parsed.events) {
    pushLine(lines, 'BEGIN:VEVENT');
    pushLine(lines, `UID:${escapeIcsText(event.uid)}`);
    pushLine(lines, `DTSTAMP:${generatedAt}`);
    pushLine(lines, `LAST-MODIFIED:${formatIcsDate(event.updatedAt)}`);
    pushLine(lines, `SUMMARY:${escapeIcsText(event.title)}`);

    if (event.startAt && new Date(event.startAt) < new Date(event.dueAt)) {
      pushLine(lines, `DTSTART:${formatIcsDate(event.startAt)}`);
      pushLine(lines, `DTEND:${formatIcsDate(event.dueAt)}`);
    } else {
      pushLine(lines, `DTSTART:${formatIcsDate(event.dueAt)}`);
    }

    if (event.url) {
      pushLine(lines, `URL:${event.url}`);
    }

    pushLine(lines, 'END:VEVENT');
  }

  pushLine(lines, 'END:VCALENDAR');
  return `${lines.join('\r\n')}\r\n`;
}
